import type { AlarmEscalationSettings, OrganisationBranding } from '@empower-safety/shared-types';
import { Bell, CheckCircle2, Loader2, Pencil, Phone, Plus, Save, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  type EmergencyContactDto,
  useCreateOrganisationContact,
  useDeleteContact,
  useOrganisationEmergencyContacts,
  useUpdateContact,
} from '../api/useEmergencyContacts.js';
import {
  useEscalationSettings,
  useUpdateEscalationSettings,
} from '../api/useEscalationSettings.js';
import {
  type JobTitleDto,
  useAllJobTitles,
  useCreateJobTitle,
  useJobTitleUsage,
  useUpdateJobTitle,
} from '../api/useJobTitles.js';
import { useMe } from '../api/useMe.js';
import {
  type DayOfWeek,
  type NotificationChannel,
  type NotificationPreferences,
  useMyNotificationPreferences,
  useUpdateMyNotificationPreferences,
} from '../api/useNotificationPreferences.js';
import {
  type OrganisationDto,
  useCurrentOrganisation,
  usePatchOrganisation,
} from '../api/useOrganisation.js';
import {
  useUpdateVisitAutoCheckinSettings,
  useVisitAutoCheckinSettings,
} from '../api/useVisitAutoCheckinSettings.js';
import { HrisIntegrationsSection } from '../components/HrisIntegrationsSection.js';
import { RosterIntegrationsSection } from '../components/RosterIntegrationsSection.js';
import { cn } from '../lib/cn.js';

// Settings — organisation-level config. Admins can edit the name, timezone, and branding;
// monitors see the same page in read-only mode. Site management + integrations will
// arrive as separate sections in follow-up work.

export default function Settings() {
  const me = useMe();
  const org = useCurrentOrganisation();
  const canEdit = me.data?.app_role === 'admin';

  if (org.isLoading) {
    return <Skeleton />;
  }
  if (org.isError || !org.data) {
    return (
      <div className="mx-auto max-w-3xl rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
        Failed to load organisation: {(org.error as Error | undefined)?.message ?? 'not found'}
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold">Settings</h1>
        <p className="text-sm text-ink-faint">
          {canEdit
            ? 'Manage your organisation profile and branding.'
            : 'View your organisation profile. Ask an admin to make changes.'}
        </p>
      </div>

      <OrgProfileSection org={org.data} canEdit={canEdit} />
      <BrandingSection org={org.data} canEdit={canEdit} />
      <OrganisationContactsSection canEdit={canEdit} />
      <EscalationSection canEdit={canEdit} />
      <VisitAutoCheckinSection canEdit={canEdit} />
      <JobTitlesSection canEdit={canEdit} />
      <NotificationPreferencesSection />
      <RosterIntegrationsSection canEdit={canEdit} />
      <HrisIntegrationsSection canEdit={canEdit} />
      <ReadOnlySection org={org.data} />
    </div>
  );
}

// --------------------------------------------------------------------------
// Organisation-wide emergency contacts (e.g. monitoring centre, on-call mgr)
// --------------------------------------------------------------------------

function OrganisationContactsSection({ canEdit }: { canEdit: boolean }) {
  const contacts = useOrganisationEmergencyContacts();
  const [showAdd, setShowAdd] = useState(false);

  return (
    <Card title="Organisation-wide emergency contacts">
      <p className="-mt-2 mb-3 text-xs text-ink-faint">
        Contacts that get notified for <strong>every</strong> alarm in this organisation. Typical
        examples: monitoring centre, on-call manager. Use escalation tiers (notify-after-N-minutes)
        to route alarms to backups when the primary doesn't acknowledge.
      </p>
      {contacts.isLoading && <div className="text-xs text-ink-faint">Loading…</div>}
      {contacts.data && contacts.data.contacts.length === 0 && (
        <div className="rounded-md border border-dashed border-line bg-surface-3 p-3 text-xs text-ink-faint">
          No org-wide contacts yet.
        </div>
      )}
      {contacts.data && contacts.data.contacts.length > 0 && (
        <ul className="divide-y divide-line rounded-md border border-line">
          {contacts.data.contacts.map((c) => (
            <OrgContactRow key={c.id} contact={c} canEdit={canEdit} />
          ))}
        </ul>
      )}
      {canEdit && (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-purple hover:underline"
        >
          <Plus className="h-3 w-3" /> Add organisation-wide contact
        </button>
      )}
      {showAdd && <AddOrgContactModal onClose={() => setShowAdd(false)} />}
    </Card>
  );
}

function OrgContactRow({
  contact,
  canEdit,
}: {
  contact: EmergencyContactDto;
  canEdit: boolean;
}) {
  const update = useUpdateContact();
  const del = useDeleteContact();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(contact.name);
  const [phone, setPhone] = useState(contact.phone_e164 ?? '');
  const [email, setEmail] = useState(contact.email ?? '');
  const [tierMin, setTierMin] = useState(Math.round(contact.notify_after_seconds / 60));

  if (editing) {
    return (
      <li className="bg-brand-purple/5 p-3">
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            await update.mutateAsync({
              id: contact.id,
              name: name !== contact.name ? name : undefined,
              phone_e164: (phone || null) !== contact.phone_e164 ? phone || null : undefined,
              email: (email || null) !== contact.email ? email || null : undefined,
              notify_after_seconds:
                tierMin * 60 !== contact.notify_after_seconds ? tierMin * 60 : undefined,
            });
            setEditing(false);
          }}
          className="flex flex-col gap-2"
        >
          <div className="grid grid-cols-2 gap-2">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="rounded border border-line-strong px-2 py-1 text-xs"
              placeholder="Name"
            />
            <input
              type="number"
              min={0}
              value={tierMin}
              onChange={(e) => setTierMin(Math.max(0, Number(e.target.value) || 0))}
              className="rounded border border-line-strong px-2 py-1 text-xs"
              placeholder="Tier (minutes)"
            />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="rounded border border-line-strong px-2 py-1 font-mono text-xs"
              placeholder="+61…"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded border border-line-strong px-2 py-1 font-mono text-xs"
              placeholder="email@…"
            />
          </div>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="text-xs text-ink-faint hover:underline"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={update.isPending}
              className="rounded bg-brand-purple px-2 py-1 text-xs font-semibold text-white hover:bg-brand-deep-purple"
            >
              Save
            </button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-3 px-3 py-2">
      <Phone className="h-4 w-4 text-ink-mute" />
      <div className="flex-1 text-sm">
        <div className="font-medium">
          {contact.name}
          {contact.role && <span className="ml-2 text-xs text-ink-faint">· {contact.role}</span>}
        </div>
        <div className="text-xs text-ink-faint">
          {contact.phone_e164 && <span className="font-mono">{contact.phone_e164}</span>}
          {contact.email && <span className="ml-2 font-mono">{contact.email}</span>}
          {contact.notify_after_seconds > 0 && (
            <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-700">
              <Bell className="h-2.5 w-2.5" />
              tier {Math.round(contact.notify_after_seconds / 60)} min
            </span>
          )}
        </div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-ink-faint hover:text-ink"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (!confirm(`Delete contact "${contact.name}"?`)) return;
              del.mutate(contact.id);
            }}
            className="text-red-700 hover:text-red-900"
          >
            <Trash2 className="h-3 w-3" />
          </button>
        </div>
      )}
    </li>
  );
}

function AddOrgContactModal({ onClose }: { onClose: () => void }) {
  const create = useCreateOrganisationContact();
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [tierMin, setTierMin] = useState(0);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || (!phone.trim() && !email.trim())) return;
    await create.mutateAsync({
      name: name.trim(),
      role: role.trim() || null,
      phone_e164: phone.trim() || null,
      email: email.trim() || null,
      notify_after_seconds: tierMin * 60,
    });
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <form onSubmit={onSubmit} className="w-full max-w-md rounded-lg bg-white p-5 shadow-panel">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold">Add organisation contact</h2>
          <button type="button" onClick={onClose} className="text-ink-mute hover:text-ink-dim">
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="flex flex-col gap-3">
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Name"
            required
            className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm"
          />
          <input
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Role (e.g. Monitoring centre, On-call manager)"
            className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm"
          />
          <div className="grid grid-cols-2 gap-2">
            <input
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+61…"
              className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm font-mono"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@…"
              className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm font-mono"
            />
          </div>
          <label className="flex flex-col gap-1">
            <span className="text-xs font-medium text-ink-dim">
              Escalation tier — notify after N minutes (0 = immediate)
            </span>
            <input
              type="number"
              min={0}
              value={tierMin}
              onChange={(e) => setTierMin(Math.max(0, Number(e.target.value) || 0))}
              className="rounded-md border border-line-strong px-2.5 py-1.5 text-sm"
            />
          </label>
          {create.isError && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
              {(create.error as Error).message}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md px-3 py-1.5 text-sm text-ink-faint hover:bg-surface-3"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={create.isPending}
              className="rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
            >
              Create contact
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

// --------------------------------------------------------------------------
// Per-user notification preferences — drives channel + active-hours selection
// when this user is the recipient_user_id of an emergency contact.
// --------------------------------------------------------------------------

const ALL_DAYS: DayOfWeek[] = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const ALL_CHANNELS: NotificationChannel[] = ['sms', 'push', 'email'];
const TIMEZONES = [
  'Australia/Sydney',
  'Australia/Melbourne',
  'Australia/Brisbane',
  'Australia/Adelaide',
  'Australia/Perth',
  'Australia/Hobart',
  'Australia/Darwin',
];

function NotificationPreferencesSection() {
  const query = useMyNotificationPreferences();
  const update = useUpdateMyNotificationPreferences();

  const initial: NotificationPreferences = query.data?.preferences ?? {
    timezone: 'Australia/Sydney',
    business_hours: { days: ['mon', 'tue', 'wed', 'thu', 'fri'], start: '09:00', end: '17:00' },
    channels: { business_hours: ['sms', 'push'], after_hours: ['sms'] },
  };

  const [tz, setTz] = useState(initial.timezone ?? 'Australia/Sydney');
  const [days, setDays] = useState<DayOfWeek[]>(
    initial.business_hours?.days ?? ['mon', 'tue', 'wed', 'thu', 'fri'],
  );
  const [start, setStart] = useState(initial.business_hours?.start ?? '09:00');
  const [end, setEnd] = useState(initial.business_hours?.end ?? '17:00');
  const [bhChannels, setBhChannels] = useState<NotificationChannel[]>(
    initial.channels?.business_hours ?? ['sms', 'push'],
  );
  const [ahChannels, setAhChannels] = useState<NotificationChannel[]>(
    initial.channels?.after_hours ?? ['sms'],
  );

  // Re-sync when query data loads.
  useEffect(() => {
    if (!query.data?.preferences) return;
    const p = query.data.preferences;
    if (p.timezone) setTz(p.timezone);
    if (p.business_hours?.days) setDays(p.business_hours.days);
    if (p.business_hours?.start) setStart(p.business_hours.start);
    if (p.business_hours?.end) setEnd(p.business_hours.end);
    if (p.channels?.business_hours) setBhChannels(p.channels.business_hours);
    if (p.channels?.after_hours) setAhChannels(p.channels.after_hours);
  }, [query.data]);

  function toggleDay(d: DayOfWeek) {
    setDays((prev) => (prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]));
  }
  function toggleChannel(
    arr: NotificationChannel[],
    setArr: (v: NotificationChannel[]) => void,
    ch: NotificationChannel,
  ) {
    setArr(arr.includes(ch) ? arr.filter((x) => x !== ch) : [...arr, ch]);
  }

  async function save() {
    await update.mutateAsync({
      timezone: tz,
      business_hours: { days, start, end },
      channels: { business_hours: bhChannels, after_hours: ahChannels },
    });
  }

  return (
    <Card title="My notification preferences">
      <p className="-mt-2 mb-3 text-xs text-ink-faint">
        When you're listed as a recipient on an emergency contact, these settings decide which
        channels we use and when. Set the after-hours channels to an empty selection to mute
        notifications outside business hours entirely.
      </p>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Timezone">
          <select value={tz} onChange={(e) => setTz(e.target.value)} className={inputClass(true)}>
            {TIMEZONES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </Field>
        <div />
        <Field label="Business hours start">
          <input
            type="time"
            value={start}
            onChange={(e) => setStart(e.target.value)}
            className={inputClass(true)}
          />
        </Field>
        <Field label="Business hours end">
          <input
            type="time"
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            className={inputClass(true)}
          />
        </Field>
      </div>

      <div className="mt-4">
        <span className="text-xs font-medium text-ink-dim">Business days</span>
        <div className="mt-1 flex flex-wrap gap-1.5">
          {ALL_DAYS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => toggleDay(d)}
              className={cn(
                'rounded-md px-2.5 py-1 text-xs font-medium uppercase ring-1 ring-inset',
                days.includes(d)
                  ? 'bg-brand-purple text-white ring-brand-purple'
                  : 'bg-white text-ink-faint ring-gray-300 hover:bg-surface-3',
              )}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3">
        <ChannelGroup
          label="Business hours channels"
          value={bhChannels}
          onToggle={(ch) => toggleChannel(bhChannels, setBhChannels, ch)}
        />
        <ChannelGroup
          label="After hours channels"
          value={ahChannels}
          onToggle={(ch) => toggleChannel(ahChannels, setAhChannels, ch)}
          mutedHint
        />
      </div>

      <div className="mt-4 flex items-center justify-end gap-2">
        {update.isError && (
          <span className="text-xs text-red-700">
            Save failed: {(update.error as Error).message}
          </span>
        )}
        {update.isSuccess && !update.isPending && (
          <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
            <CheckCircle2 className="h-3 w-3" /> Saved
          </span>
        )}
        <button
          type="button"
          onClick={save}
          disabled={update.isPending}
          className="inline-flex items-center gap-1 rounded-md bg-brand-purple px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
        >
          {update.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Save className="h-3.5 w-3.5" />
          )}
          Save preferences
        </button>
      </div>
    </Card>
  );
}

function ChannelGroup({
  label,
  value,
  onToggle,
  mutedHint,
}: {
  label: string;
  value: NotificationChannel[];
  onToggle: (ch: NotificationChannel) => void;
  mutedHint?: boolean;
}) {
  return (
    <div>
      <span className="text-xs font-medium text-ink-dim">{label}</span>
      <div className="mt-1 flex flex-wrap gap-1.5">
        {ALL_CHANNELS.map((ch) => (
          <button
            key={ch}
            type="button"
            onClick={() => onToggle(ch)}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium uppercase ring-1 ring-inset',
              value.includes(ch)
                ? 'bg-brand-purple text-white ring-brand-purple'
                : 'bg-white text-ink-faint ring-gray-300 hover:bg-surface-3',
            )}
          >
            {ch}
          </button>
        ))}
      </div>
      {mutedHint && value.length === 0 && (
        <p className="mt-1 text-[10px] text-amber-700">
          Muted: you won't receive after-hours alerts.
        </p>
      )}
    </div>
  );
}

function OrgProfileSection({ org, canEdit }: { org: OrganisationDto; canEdit: boolean }) {
  const [name, setName] = useState(org.name);
  const [timezone, setTimezone] = useState(org.timezone);
  const patch = usePatchOrganisation();
  const dirty = name !== org.name || timezone !== org.timezone;

  useEffect(() => {
    setName(org.name);
    setTimezone(org.timezone);
  }, [org]);

  return (
    <Card title="Organisation">
      <Field label="Name">
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={!canEdit}
          className={inputClass(canEdit)}
        />
      </Field>
      <Field label="Timezone" hint="e.g. Australia/Sydney">
        <input
          type="text"
          value={timezone}
          onChange={(e) => setTimezone(e.target.value)}
          disabled={!canEdit}
          className={inputClass(canEdit)}
        />
      </Field>
      <Field label="Plan">
        <input
          type="text"
          value={`${org.plan} · ${org.billing_status}`}
          disabled
          className={inputClass(false)}
        />
      </Field>
      {canEdit && (
        <SaveBar
          dirty={dirty}
          saving={patch.isPending}
          onSave={() => patch.mutate({ name, timezone })}
          error={patch.error}
          success={patch.isSuccess && !dirty}
        />
      )}
    </Card>
  );
}

function clampMinutes(v: number): number {
  if (!Number.isFinite(v)) return 1;
  return Math.min(60, Math.max(1, Math.trunc(v)));
}

function EscalationSection({ canEdit }: { canEdit: boolean }) {
  const settings = useEscalationSettings();
  const patch = useUpdateEscalationSettings();
  const [enabled, setEnabled] = useState(true);
  const [delayMin, setDelayMin] = useState(5);
  const [intervalMin, setIntervalMin] = useState(5);
  const [maxRepeats, setMaxRepeats] = useState(3);
  const [target, setTarget] = useState<AlarmEscalationSettings['target']>('all_contacts');

  useEffect(() => {
    const d = settings.data;
    if (!d) return;
    setEnabled(d.enabled);
    setDelayMin(Math.round(d.delay_seconds / 60));
    setIntervalMin(Math.round(d.repeat_interval_seconds / 60));
    setMaxRepeats(d.max_repeats);
    setTarget(d.target);
  }, [settings.data]);

  const d = settings.data;
  if (!d) return null;

  const dirty =
    enabled !== d.enabled ||
    delayMin * 60 !== d.delay_seconds ||
    intervalMin * 60 !== d.repeat_interval_seconds ||
    maxRepeats !== d.max_repeats ||
    target !== d.target;

  return (
    <Card title="Alarm escalation">
      <p className="text-sm text-ink-faint">
        If nobody acknowledges an alarm, re-notify emergency contacts on a schedule until someone
        responds. Changes apply immediately, including to alarms already in progress.
      </p>
      <Field label="Escalation">
        <div className="flex gap-2">
          {([true, false] as const).map((v) => (
            <button
              key={String(v)}
              type="button"
              disabled={!canEdit}
              onClick={() => setEnabled(v)}
              className={cn(
                'rounded-md px-4 py-1.5 text-sm font-medium ring-1 transition',
                enabled === v
                  ? 'bg-brand-purple text-white ring-brand-purple'
                  : 'bg-white text-ink-faint ring-gray-300',
                !canEdit && 'cursor-not-allowed opacity-60',
              )}
            >
              {v ? 'On' : 'Off'}
            </button>
          ))}
        </div>
      </Field>
      {enabled && (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="First escalation after" hint="Minutes unacknowledged (1–60)">
              <input
                type="number"
                min={1}
                max={60}
                value={delayMin}
                onChange={(e) => setDelayMin(clampMinutes(Number(e.target.value)))}
                disabled={!canEdit}
                className={inputClass(canEdit)}
              />
            </Field>
            <Field label="Repeat every" hint="Minutes between repeats (1–60)">
              <input
                type="number"
                min={1}
                max={60}
                value={intervalMin}
                onChange={(e) => setIntervalMin(clampMinutes(Number(e.target.value)))}
                disabled={!canEdit}
                className={inputClass(canEdit)}
              />
            </Field>
            <Field label="Max repeats" hint="Rounds before giving up (0–10)">
              <input
                type="number"
                min={0}
                max={10}
                value={maxRepeats}
                onChange={(e) =>
                  setMaxRepeats(Math.min(10, Math.max(0, Math.trunc(Number(e.target.value) || 0))))
                }
                disabled={!canEdit}
                className={inputClass(canEdit)}
              />
            </Field>
          </div>
          <Field label="Escalate to">
            <select
              value={target}
              onChange={(e) => setTarget(e.target.value as AlarmEscalationSettings['target'])}
              disabled={!canEdit}
              className={inputClass(canEdit)}
            >
              <option value="all_contacts">All emergency contacts</option>
              <option value="org_contacts">Organisation contacts only (monitoring desk)</option>
            </select>
          </Field>
        </>
      )}
      {canEdit && (
        <SaveBar
          dirty={dirty}
          saving={patch.isPending}
          onSave={() =>
            patch.mutate({
              enabled,
              delay_seconds: delayMin * 60,
              repeat_interval_seconds: intervalMin * 60,
              max_repeats: maxRepeats,
              target,
            })
          }
          error={patch.error}
          success={patch.isSuccess && !dirty}
        />
      )}
    </Card>
  );
}

function VisitAutoCheckinSection({ canEdit }: { canEdit: boolean }) {
  const settings = useVisitAutoCheckinSettings();
  const patch = useUpdateVisitAutoCheckinSettings();
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    if (settings.data) setEnabled(settings.data.enabled);
  }, [settings.data]);

  const d = settings.data;
  if (!d) return null;
  const dirty = enabled !== d.enabled;

  return (
    <Card title="Visit auto check-in">
      <p className="text-sm text-ink-faint">
        When an AlayaCare visit is marked Arrived, automatically open a welfare check-in for the
        length of the visit and close it when the clinician leaves. Workers can opt out of the
        auto-close in their app. Off by default; rolled out organisation by organisation.
      </p>
      <Field label="Auto check-in on visit arrival">
        <div className="flex gap-2">
          {([true, false] as const).map((v) => (
            <button
              key={String(v)}
              type="button"
              disabled={!canEdit}
              onClick={() => setEnabled(v)}
              className={cn(
                'rounded-md px-4 py-1.5 text-sm font-medium ring-1 transition',
                enabled === v
                  ? 'bg-brand-purple text-white ring-brand-purple'
                  : 'bg-white text-ink-faint ring-gray-300',
                !canEdit && 'cursor-not-allowed opacity-60',
              )}
            >
              {v ? 'On' : 'Off'}
            </button>
          ))}
        </div>
      </Field>
      {canEdit && (
        <SaveBar
          dirty={dirty}
          saving={patch.isPending}
          onSave={() => patch.mutate({ enabled })}
          error={patch.error}
          success={patch.isSuccess && !dirty}
        />
      )}
    </Card>
  );
}

function BrandingSection({ org, canEdit }: { org: OrganisationDto; canEdit: boolean }) {
  const initial = org.branding ?? {};
  const [branding, setBranding] = useState<OrganisationBranding>(initial);
  const patch = usePatchOrganisation();
  const dirty = JSON.stringify(branding) !== JSON.stringify(initial);

  useEffect(() => {
    setBranding(org.branding ?? {});
  }, [org]);

  function update<K extends keyof OrganisationBranding>(key: K, value: OrganisationBranding[K]) {
    setBranding((b) => ({ ...b, [key]: value }));
  }

  return (
    <Card title="Branding">
      <Field
        label="App display name"
        hint="Appears in welcome screens, emails, and SMS sender ID fallback."
      >
        <input
          type="text"
          value={branding.app_display_name ?? ''}
          onChange={(e) => update('app_display_name', e.target.value)}
          disabled={!canEdit}
          placeholder="Empower Safety"
          className={inputClass(canEdit)}
        />
      </Field>
      <Field
        label="SMS sender ID"
        hint="Alpha sender (up to 11 chars). Some carriers ignore and use numeric instead."
      >
        <input
          type="text"
          value={branding.sms_sender_id ?? ''}
          onChange={(e) => update('sms_sender_id', e.target.value)}
          disabled={!canEdit}
          maxLength={11}
          placeholder="EmpowerSafe"
          className={inputClass(canEdit)}
        />
      </Field>
      <Field label="Primary colour">
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={branding.primary_color ?? '#4C12A1'}
            onChange={(e) => update('primary_color', e.target.value)}
            disabled={!canEdit}
            className="h-9 w-12 cursor-pointer rounded border border-line-strong disabled:cursor-not-allowed disabled:opacity-50"
          />
          <input
            type="text"
            value={branding.primary_color ?? ''}
            onChange={(e) => update('primary_color', e.target.value)}
            disabled={!canEdit}
            placeholder="#4C12A1"
            className={cn(inputClass(canEdit), 'font-mono w-32')}
          />
        </div>
      </Field>
      <Field label="Secondary colour">
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={branding.secondary_color ?? '#5BC2E7'}
            onChange={(e) => update('secondary_color', e.target.value)}
            disabled={!canEdit}
            className="h-9 w-12 cursor-pointer rounded border border-line-strong disabled:cursor-not-allowed disabled:opacity-50"
          />
          <input
            type="text"
            value={branding.secondary_color ?? ''}
            onChange={(e) => update('secondary_color', e.target.value)}
            disabled={!canEdit}
            placeholder="#5BC2E7"
            className={cn(inputClass(canEdit), 'font-mono w-32')}
          />
        </div>
      </Field>
      <Field label="Logo URL" hint="Square image preferred, served over HTTPS.">
        <input
          type="url"
          value={branding.logo_url ?? ''}
          onChange={(e) => update('logo_url', e.target.value)}
          disabled={!canEdit}
          placeholder="https://…/logo.png"
          className={inputClass(canEdit)}
        />
      </Field>
      {canEdit && (
        <SaveBar
          dirty={dirty}
          saving={patch.isPending}
          onSave={() => patch.mutate({ branding })}
          error={patch.error}
          success={patch.isSuccess && !dirty}
        />
      )}
    </Card>
  );
}

function ReadOnlySection({ org }: { org: OrganisationDto }) {
  return (
    <Card title="System">
      <Field label="Organisation ID">
        <input value={org.id} disabled className={cn(inputClass(false), 'font-mono text-xs')} />
      </Field>
      <Field label="Clerk organisation ID">
        <input
          value={org.clerk_org_id}
          disabled
          className={cn(inputClass(false), 'font-mono text-xs')}
        />
      </Field>
      <Field label="Slug">
        <input value={org.slug} disabled className={inputClass(false)} />
      </Field>
      <Field label="Type">
        <input value={org.type} disabled className={inputClass(false)} />
      </Field>
      <Field label="SSO">
        <input
          value={org.sso_enabled ? 'Enabled' : 'Not configured'}
          disabled
          className={inputClass(false)}
        />
      </Field>
    </Card>
  );
}

// --------------------------------------------------------------------------
// Job titles — per-organisation list surfaced in the Roster worker-edit
// dropdown. HRIS-synced titles appear here too, tagged with a small "HRIS"
// badge so admins can tell which ones came from a payroll feed.
// --------------------------------------------------------------------------

function JobTitlesSection({ canEdit }: { canEdit: boolean }) {
  const jobTitles = useAllJobTitles();
  const [showAdd, setShowAdd] = useState(false);

  const rows = jobTitles.data?.job_titles ?? [];
  const active = rows.filter((t) => t.is_active);
  const archived = rows.filter((t) => !t.is_active);

  return (
    <Card title="Job titles">
      <p className="-mt-2 mb-3 text-xs text-ink-faint">
        These appear in the worker-edit dropdown on Roster and as part of each worker's badge (e.g.
        "VIC Registered Nurse"). Titles added by an HRIS integration are tagged HRIS. Archived
        titles stay attached to existing workers but disappear from the dropdown.
      </p>
      {jobTitles.isLoading && <div className="text-xs text-ink-faint">Loading…</div>}
      {jobTitles.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
          Failed to load job titles: {(jobTitles.error as Error | undefined)?.message ?? 'unknown'}
        </div>
      )}
      {!jobTitles.isLoading && active.length === 0 && (
        <div className="rounded-md border border-dashed border-line bg-surface-3 p-3 text-xs text-ink-faint">
          No active job titles yet.
        </div>
      )}
      {active.length > 0 && (
        <ul className="divide-y divide-line rounded-md border border-line">
          {active.map((t) => (
            <JobTitleRow key={t.id} jobTitle={t} canEdit={canEdit} />
          ))}
        </ul>
      )}
      {archived.length > 0 && (
        <details className="mt-3">
          <summary className="cursor-pointer text-xs font-medium text-ink-faint hover:text-ink">
            {archived.length} archived {archived.length === 1 ? 'title' : 'titles'}
          </summary>
          <ul className="mt-2 divide-y divide-line rounded-md border border-line">
            {archived.map((t) => (
              <JobTitleRow key={t.id} jobTitle={t} canEdit={canEdit} />
            ))}
          </ul>
        </details>
      )}
      {canEdit && !showAdd && (
        <button
          type="button"
          onClick={() => setShowAdd(true)}
          className="mt-3 inline-flex items-center gap-1 text-xs font-medium text-brand-purple hover:underline"
        >
          <Plus className="h-3 w-3" /> Add job title
        </button>
      )}
      {showAdd && <AddJobTitleForm onClose={() => setShowAdd(false)} />}
    </Card>
  );
}

function JobTitleRow({ jobTitle, canEdit }: { jobTitle: JobTitleDto; canEdit: boolean }) {
  const update = useUpdateJobTitle(jobTitle.id);
  const usage = useJobTitleUsage(canEdit ? jobTitle.id : undefined);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(jobTitle.name);
  const [error, setError] = useState<string | null>(null);

  async function onSave() {
    setError(null);
    const trimmed = name.trim();
    if (!trimmed || trimmed === jobTitle.name) {
      setEditing(false);
      setName(jobTitle.name);
      return;
    }
    try {
      await update.mutateAsync({ name: trimmed });
      setEditing(false);
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to save');
    }
  }

  if (editing) {
    return (
      <li className="bg-brand-purple/5 p-3">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onSave();
          }}
          className="flex flex-col gap-2"
        >
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            // biome-ignore lint/a11y/noAutofocus: opens in response to a click on Edit; focus should land in the only input
            autoFocus
            className="rounded border border-line-strong px-2 py-1 text-sm"
            placeholder="Job title"
          />
          {error && <div className="text-xs text-red-700">{error}</div>}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setEditing(false);
                setName(jobTitle.name);
                setError(null);
              }}
              className="text-xs text-ink-faint hover:underline"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={update.isPending}
              className="rounded bg-brand-purple px-2 py-1 text-xs font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
            >
              Save
            </button>
          </div>
        </form>
      </li>
    );
  }

  return (
    <li className="flex items-center gap-3 px-3 py-2">
      <div className="flex-1 text-sm">
        <span className={cn('font-medium', !jobTitle.is_active && 'text-ink-faint line-through')}>
          {jobTitle.name}
        </span>
        {jobTitle.source === 'hris' && (
          <span className="ml-2 inline-flex items-center rounded-full bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-blue-700">
            HRIS
          </span>
        )}
        {canEdit && usage.data && usage.data.worker_count > 0 && (
          <span className="ml-2 text-xs text-ink-faint">
            {usage.data.worker_count} {usage.data.worker_count === 1 ? 'worker' : 'workers'}
          </span>
        )}
      </div>
      {canEdit && (
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setEditing(true)}
            className="text-ink-faint hover:text-ink"
            title="Rename"
          >
            <Pencil className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => {
              const used = usage.data?.worker_count ?? 0;
              if (jobTitle.is_active) {
                const warn =
                  used > 0
                    ? `Archive "${jobTitle.name}"? ${used} ${used === 1 ? 'worker keeps' : 'workers keep'} this label, but new workers won't be able to select it.`
                    : `Archive "${jobTitle.name}"?`;
                if (!confirm(warn)) return;
                update.mutate({ is_active: false });
              } else {
                update.mutate({ is_active: true });
              }
            }}
            className={cn(
              'hover:text-ink',
              jobTitle.is_active ? 'text-ink-faint' : 'text-brand-purple',
            )}
            title={jobTitle.is_active ? 'Archive' : 'Unarchive'}
          >
            {jobTitle.is_active ? (
              <Trash2 className="h-3 w-3" />
            ) : (
              <CheckCircle2 className="h-3 w-3" />
            )}
          </button>
        </div>
      )}
    </li>
  );
}

function AddJobTitleForm({ onClose }: { onClose: () => void }) {
  const create = useCreateJobTitle();
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    const trimmed = name.trim();
    if (!trimmed) {
      setError('Name is required.');
      return;
    }
    try {
      await create.mutateAsync({ name: trimmed });
      onClose();
    } catch (err) {
      setError((err as Error)?.message ?? 'Failed to add');
    }
  }

  return (
    <form
      onSubmit={onSubmit}
      className="mt-3 flex flex-col gap-2 rounded-md border border-line bg-brand-purple/5 p-3"
    >
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        maxLength={80}
        // biome-ignore lint/a11y/noAutofocus: opens in response to a click on Add; focus should land in the only input
        autoFocus
        placeholder="e.g. Registered Nurse"
        className="rounded border border-line-strong px-2 py-1 text-sm"
      />
      {error && <div className="text-xs text-red-700">{error}</div>}
      <div className="flex justify-end gap-2">
        <button type="button" onClick={onClose} className="text-xs text-ink-faint hover:underline">
          Cancel
        </button>
        <button
          type="submit"
          disabled={create.isPending}
          className="rounded bg-brand-purple px-2 py-1 text-xs font-semibold text-white hover:bg-brand-deep-purple disabled:opacity-50"
        >
          Add
        </button>
      </div>
    </form>
  );
}

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="panel">
      <div className="panel-head">
        <div>
          <h2 className="font-heading text-sm font-bold text-ink">{title}</h2>
        </div>
      </div>
      <div className="panel-body space-y-4">{children}</div>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1.5 eyebrow">{label}</div>
      {children}
      {hint && <div className="mt-1 text-xs text-ink-mute">{hint}</div>}
    </label>
  );
}

function inputClass(enabled: boolean) {
  return cn(
    'w-full rounded-md border px-3 py-2 text-sm transition',
    enabled
      ? 'border-line-strong bg-white focus:border-brand-purple focus:outline-none focus:ring-1 focus:ring-brand-purple'
      : 'cursor-not-allowed border-line bg-surface-3 text-ink-faint',
  );
}

function SaveBar({
  dirty,
  saving,
  onSave,
  error,
  success,
}: {
  dirty: boolean;
  saving: boolean;
  onSave: () => void;
  error: unknown;
  success: boolean;
}) {
  return (
    <div className="flex items-center justify-between border-t border-line pt-4">
      <div className="text-xs">
        {error ? (
          <span className="text-red-700">{(error as Error).message}</span>
        ) : success ? (
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <CheckCircle2 className="h-3 w-3" /> Saved
          </span>
        ) : (
          <span className="text-ink-mute">{dirty ? 'Unsaved changes' : 'Up to date'}</span>
        )}
      </div>
      <button
        type="button"
        onClick={onSave}
        disabled={!dirty || saving}
        className="inline-flex items-center gap-1.5 rounded-md bg-brand-purple px-4 py-2 text-sm font-medium text-white transition hover:bg-brand-purple/90 disabled:opacity-50"
      >
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
        {saving ? 'Saving…' : 'Save changes'}
      </button>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="mx-auto max-w-3xl animate-pulse space-y-6">
      <div className="h-8 w-48 rounded bg-gray-200" />
      <div className="h-40 rounded-lg bg-gray-200" />
      <div className="h-60 rounded-lg bg-gray-200" />
    </div>
  );
}
